import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { bpHistory } from 'global/bpHistory';

import { CensusEnrollmentModule } from 'store/modules/census/enrollment-based/censusEnrollmentModule';
import ElectionsModule from 'store/modules/elections/electionsModule';
import ICensusUploadPageStore from 'store/modules/census/iCensusUploadPageStore';

interface DomainProps {
  groupId: string;
  enrollmentPeriodId: string;
}
export default class CensusUploadPageStore extends ICensusUploadPageStore<DomainProps, CensusEnrollmentModule> {
  private _electionsModule: ElectionsModule;
  protected _censusModule: CensusEnrollmentModule;
  protected _domainData: DomainProps;

  public get electionsModule(): ElectionsModule {
    return this._electionsModule;
  }

  public async getCensusData(): Promise<void> {
    this.setIsLoading(true);
    await Promise.all([
      this._censusModule.getRateSegmentData(),
      this._electionsModule.getStatistics(),
      this._censusModule.getPreviouslyUploadedFiles(),
      this._censusModule.uploadExistingCensus(),
    ]);
    runInAction(() => {
      this.setIsLoading(false);
    });
  }

  public onCompleteRedirect(): void {
    bpHistory.push(`/add-employees/census-review/${this._domainData.groupId}/${this._domainData.enrollmentPeriodId}`);
  }

  constructor(domainData: DomainProps) {
    super();
    this._censusModule = new CensusEnrollmentModule({
      groupId: domainData.groupId,
      enrollmentPeriodId: domainData.enrollmentPeriodId,
    });
    this._electionsModule = new ElectionsModule({
      groupId: domainData.groupId,
      enrollmentPeriodId: domainData.enrollmentPeriodId,
    });
    this._domainData = domainData;

    makeObservable<CensusUploadPageStore, '_censusModule' | '_electionsModule' | '_domainData'>(this, {
      _censusModule: observable,
      _electionsModule: observable,
      _domainData: observable,

      electionsModule: computed,

      getCensusData: action.bound,
      onCompleteRedirect: action.bound,
    });
  }
}

const CensusUploadPageStoreContext = createContext(new CensusUploadPageStore({} as DomainProps));

export const useCensusUploadPageStore = (): CensusUploadPageStore => {
  return useContext(CensusUploadPageStoreContext);
};

export { CensusUploadPageStoreContext };
